'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

import { Icon, IconComponent, Paragraph } from '@graftcode/design-system'
import * as Dialog from '@radix-ui/react-dialog'
import clsx from 'clsx'
import { useShallow } from 'zustand/react/shallow'

import { resolveAppUrl } from '@common/utils/resolveAppUrl'

import { selectGraftcodeOnboardingPostTutorialPersonalization } from '@apps/graftcode-onboarding/stores/graftcode-onboarding.selector'
import { useGraftcodeOnboardingStore } from '@apps/graftcode-onboarding/stores/graftcode-onboarding.store'

import { GRAFTCODE_ONBOARDING_ASSETS_BASE } from '../../graftcode-onboarding.const'
import { GRAFTCODE_ONBOARDING_TEST_IDS } from '../../graftcode-onboarding-test-ids.const'
import {
  GRAFTCODE_ONBOARDING_DASHBOARD_VIDEOS,
  type GraftcodeOnboardingDashboardVideo,
} from '../../graftcode-onboarding-videos.const'
import { getPersonalizedOnboardingDashboardVideos } from '../../lib/getPersonalizedOnboardingDashboardVideos'
import { prefetchOnboardingGuideForScenario } from '../../lib/onboarding-tutorial-content-cache'
import { scrollGraftcodeOnboardingToTop } from '../../lib/scrollGraftcodeOnboardingToTop'
import { getPersonalizedOnboardingCourses } from '../../mappers/getPersonalizedOnboardingCourses'

import styles from './GraftcodeOnboardingPostTutorialDashboard.module.scss'

const SCROLL_STEP_PX = 256

export function GraftcodeOnboardingPostTutorialDashboard() {
  const t = useTranslations('graftcodeOnboarding')
  const {
    completedScenarioIds,
    goalId,
    setupMode,
    primaryScenarioId,
    lastCompletedStackId,
    selectedStackId,
    completedStacksByScenario,
    resumeGuideForScenario,
  } = useGraftcodeOnboardingStore(useShallow(selectGraftcodeOnboardingPostTutorialPersonalization))

  const preferredStackId = lastCompletedStackId ?? selectedStackId

  const scrollerRef = useRef<HTMLDivElement>(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(true)

  const completedSet = useMemo(() => new Set(completedScenarioIds), [completedScenarioIds])

  const personalizedCourses = useMemo(
    () =>
      getPersonalizedOnboardingCourses({
        goalId,
        setupMode,
        primaryScenarioId,
        lastCompletedStackId: preferredStackId,
        completedScenarioIds,
        completedStacksByScenario,
      }),
    [goalId, setupMode, primaryScenarioId, preferredStackId, completedScenarioIds, completedStacksByScenario]
  )

  const personalizedVideos = useMemo(() => {
    const fromAcademy = getPersonalizedOnboardingDashboardVideos({
      goalId,
      setupMode,
      primaryScenarioId,
      lastCompletedStackId: preferredStackId,
      completedScenarioIds,
    })
    if (fromAcademy.length > 0) {
      return fromAcademy
    }
    return GRAFTCODE_ONBOARDING_DASHBOARD_VIDEOS.map((video) => ({
      ...video,
      title: video.titleKey ? t(video.titleKey) : video.title,
    }))
  }, [goalId, setupMode, primaryScenarioId, preferredStackId, completedScenarioIds, t])

  const [activeVideo, setActiveVideo] = useState<GraftcodeOnboardingDashboardVideo | null>(null)

  const syncScrollButtons = useCallback(() => {
    const scroller = scrollerRef.current
    if (!scroller) {
      return
    }
    setCanScrollPrev(scroller.scrollLeft > 2)
    setCanScrollNext(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 2)
  }, [])

  useEffect(() => {
    syncScrollButtons()
  }, [syncScrollButtons, personalizedCourses.length])

  const scrollCourses = useCallback(
    (direction: -1 | 1) => {
      scrollerRef.current?.scrollBy({ left: direction * SCROLL_STEP_PX, behavior: 'smooth' })
      window.setTimeout(syncScrollButtons, 300)
    },
    [syncScrollButtons]
  )

  const handleCourseClick = async (scenarioId: (typeof personalizedCourses)[number]['id']) => {
    try {
      await prefetchOnboardingGuideForScenario(scenarioId, goalId)
    } catch {
      // Still open the guide; content hook will fetch if prefetch failed.
    }
    resumeGuideForScenario(scenarioId)
    scrollGraftcodeOnboardingToTop()
  }

  return (
    <div className={styles.root} data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.postTutorial}>
      <section className={styles.panel} aria-label={t('postTutorial.getStartedTitle')}>
        <header className={styles.panelHead}>
          <Paragraph size="l" weight="semi-bold" className={styles.panelTitle}>
            {t('postTutorial.getStartedTitle')}
          </Paragraph>
          <div className={styles.carouselArrows}>
            <button
              type="button"
              className={clsx(styles.carouselArrow, styles.carouselArrowPrev)}
              disabled={!canScrollPrev}
              aria-label={t('postTutorial.scrollPrev')}
              onClick={() => scrollCourses(-1)}
            >
              <IconComponent icon={Icon.ChevronLeftRegular} size={20} />
            </button>
            <button
              type="button"
              className={styles.carouselArrow}
              disabled={!canScrollNext}
              aria-label={t('postTutorial.scrollNext')}
              onClick={() => scrollCourses(1)}
            >
              <IconComponent icon={Icon.ChevronRightRegular} size={20} />
            </button>
          </div>
        </header>

        <div className={styles.carouselWindow}>
          <div ref={scrollerRef} className={styles.carouselTrack} onScroll={syncScrollButtons}>
            {personalizedCourses.map((course) => {
              const done = completedSet.has(course.id)
              const title = t(course.cardTitleKey)
              const description =
                course.stackLabel && course.descriptionForStackKey
                  ? t(course.descriptionForStackKey, { language: course.stackLabel })
                  : t(course.descriptionKey)
              return (
                <button
                  key={course.id}
                  type="button"
                  className={clsx(styles.courseCard, done && styles.courseCardDone)}
                  aria-label={done ? `${title}, ${t('postTutorial.courseCompleted')}` : title}
                  onClick={() => void handleCourseClick(course.id)}
                >
                  <span className={styles.courseTop}>
                    <span
                      className={styles.courseIcon}
                      style={done ? undefined : { backgroundColor: course.tone }}
                      aria-hidden
                    >
                      {done ? (
                        <Image
                          src={`${GRAFTCODE_ONBOARDING_ASSETS_BASE}/dashboard/check.svg`}
                          alt=""
                          width={24}
                          height={24}
                        />
                      ) : (
                        <Image src={course.icon} alt="" width={24} height={24} />
                      )}
                    </span>
                    {!done ? (
                      <Image
                        src={`${GRAFTCODE_ONBOARDING_ASSETS_BASE}/dashboard/chevron.svg`}
                        alt=""
                        width={24}
                        height={24}
                        className={styles.courseChevron}
                        aria-hidden
                      />
                    ) : null}
                  </span>
                  <span className={styles.courseText}>
                    <strong className={styles.courseTitle}>{title}</strong>
                    <small className={styles.courseDescription}>{description}</small>
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </section>

      <section className={styles.panel} aria-label={t('postTutorial.videosTitle')}>
        <header className={styles.panelHead}>
          <Paragraph size="xl" weight="semi-bold" className={styles.panelTitle}>
            {t('postTutorial.videosTitle')}
          </Paragraph>
          <a className={styles.viewAllLink} href={resolveAppUrl('academy')} target="_blank" rel="noopener noreferrer">
            {t('postTutorial.viewAllVideos')}
          </a>
        </header>

        <div className={styles.videoGrid}>
          {personalizedVideos.map((video) => (
            <button
              key={video.id}
              type="button"
              className={styles.videoCard}
              aria-label={t('postTutorial.playVideo', { title: video.title })}
              onClick={() => setActiveVideo(video)}
            >
              <span className={styles.videoThumb}>
                <Image
                  src={video.thumbSrc}
                  alt=""
                  fill
                  className={styles.videoThumbArt}
                  sizes="240px"
                  unoptimized={video.thumbSrc.startsWith('https://')}
                />
                <Image
                  src={`${GRAFTCODE_ONBOARDING_ASSETS_BASE}/video-play-badge.svg`}
                  alt=""
                  width={32}
                  height={32}
                  className={styles.videoPlayBadge}
                />
              </span>
              <strong className={styles.videoTitle}>{video.title}</strong>
            </button>
          ))}
        </div>
      </section>

      <Dialog.Root open={Boolean(activeVideo)} onOpenChange={(open) => !open && setActiveVideo(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className={styles.videoModalOverlay} />
          <Dialog.Content className={styles.videoModalContent} aria-describedby={undefined}>
            <header className={styles.videoModalHeader}>
              <Dialog.Title className={styles.videoModalTitle}>{activeVideo?.title ?? ''}</Dialog.Title>
              <Dialog.Close className={styles.videoModalClose} aria-label={t('dashboard.closeTutorialVideo')}>
                <IconComponent icon={Icon.DismissRegular} size={20} />
              </Dialog.Close>
            </header>
            {activeVideo ? (
              <div className={styles.videoModalFrame}>
                <iframe
                  title={activeVideo.title}
                  src={activeVideo.embedSrc}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : null}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
